import {
  QuoteAction,
  QuoteState,
} from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/hooks/use-quote-state-types';

export const INITIAL_QUOTE_STATE: QuoteState = {
  account: null,
  competitorName: null,
  isDirty: false,
  lineItems: [],
  name: '',
  quoteId: null,
};

/**
 * Pure reducer for the price-quote editor state.
 *
 * All mutations set `isDirty = true` except:
 * - `LOAD_QUOTE` (replaces state wholesale; caller passes `isDirty: false`)
 * - `RESET` (returns pristine INITIAL_QUOTE_STATE)
 * - `SAVE_SUCCESS` (clears `isDirty` unless `preserveDirty` is set, which the
 *   mutator does when edits arrived during the save round-trip)
 *
 * Every line item retains a `localId` so updates can target individual rows.
 */
export const quoteReducer = (
  state: QuoteState,
  action: QuoteAction,
): QuoteState => {
  switch (action.type) {
    case 'ADD_CUSTOM_ITEM': {
      // Cannot add items without already selecting an account
      // (and account is required to determine currency code)
      if (state.account === null) {
        return state;
      }
      return {
        ...state,
        isDirty: true,
        lineItems: [
          ...state.lineItems,
          {
            ...action.item,
            // Match SET_COMPETITOR_NAME's stub so newly-added rows are
            // editable on the competitor side immediately. Caller-provided
            // competitor data wins over the stub; symmetric with ADD_ITEM.
            competitor:
              state.competitorName !== null
                ? (action.item.competitor ?? {
                    itemName: action.item.itemName,
                    price: '',
                  })
                : action.item.competitor,
            currencyCode: state.account.currencyCode,
            localId: `custom-${Date.now()}`,
            sortOrder: state.lineItems.length,
          },
        ],
      };
    }

    case 'ADD_ITEM': {
      // Cannot add items without already selecting an account
      // (and account is required to determine currency code)
      if (state.account === null) {
        return state;
      }
      // Catalog items dedupe by variantPackId; custom items (no variantPackId)
      // always append.
      if (
        action.item.variantPackId &&
        state.lineItems.some(
          (existing) => existing.variantPackId === action.item.variantPackId,
        )
      ) {
        return state;
      }
      const isCompetitive = state.competitorName !== null;
      return {
        ...state,
        isDirty: true,
        lineItems: [
          ...state.lineItems,
          {
            ...action.item,
            // Stub a competitor row when the quote is competitive so the
            // catalog item is editable on the competitor side immediately.
            competitor: isCompetitive
              ? (action.item.competitor ?? {
                  itemName: action.item.itemName,
                  price: '',
                })
              : action.item.competitor,
            currencyCode: state.account.currencyCode,
            localId: `item-${Date.now()}`,
            sortOrder: state.lineItems.length,
          },
        ],
      };
    }

    case 'LOAD_QUOTE':
      return action.state;

    case 'REMOVE_ITEM': {
      const next = state.lineItems.filter(
        (item) => item.localId !== action.localId,
      );
      if (next.length === state.lineItems.length) {
        return state;
      }
      return { ...state, isDirty: true, lineItems: next };
    }

    case 'DUPLICATE_QUOTE':
      return {
        ...state,
        isDirty: true,
        name: `${state.name}${action.suffix}`,
        quoteId: null,
      };

    case 'RESET':
      return INITIAL_QUOTE_STATE;

    case 'SAVE_SUCCESS':
      // `preserveDirty` is set by the mutator when the editor changed during
      // the round-trip; in that case we adopt the new `quoteId` but keep
      // local edits intact.
      return {
        ...state,
        isDirty: action.preserveDirty ?? false,
        name: action.name,
        quoteId: action.quoteId,
      };

    case 'SWITCH_ACCOUNT':
      // Modify the existing quote in place: swap the account, clear line
      // items + competitor. quoteId is kept so a subsequent Save updates
      // the same saved quote (creating a new draft is what New Quote /
      // RESET is for). Preserve a user-typed title — only auto-fill from
      // the new account if the current name was the auto-fill from the
      // old account.
      return {
        ...state,
        account: action.account,
        competitorName: null,
        isDirty: true,
        lineItems: [],
        name:
          state.name && state.name !== state.account?.name
            ? state.name
            : action.account.name,
      };

    case 'SET_ACCOUNT':
      return {
        ...state,
        account: action.account,
        isDirty: true,
        name: state.name || action.account.name,
      };

    case 'SET_COMPETITOR_NAME': {
      // When a competitor is named, stub an empty competitor row per line so the
      // editor can render a price column; when cleared, strip competitor data entirely.
      const isRemoving = action.name === null;
      return {
        ...state,
        competitorName: action.name,
        isDirty: true,
        lineItems: isRemoving
          ? state.lineItems.map(({ competitor: _, ...item }) => item)
          : state.lineItems.map((item) => ({
              ...item,
              competitor: item.competitor ?? {
                itemName: item.itemName,
                price: '',
              },
            })),
      };
    }

    case 'SET_NAME':
      return {
        ...state,
        isDirty: true,
        name: action.name,
      };

    case 'UPDATE_COMPETITOR':
      return {
        ...state,
        isDirty: true,
        lineItems: state.lineItems.map((item) =>
          item.localId === action.localId
            ? { ...item, competitor: action.competitor }
            : item,
        ),
      };

    case 'UPDATE_PRICE':
      return {
        ...state,
        isDirty: true,
        lineItems: state.lineItems.map((item) =>
          item.localId === action.localId
            ? { ...item, ourPrice: action.price }
            : item,
        ),
      };

    case 'UPDATE_QUANTITY':
      return {
        ...state,
        isDirty: true,
        lineItems: state.lineItems.map((item) =>
          item.localId === action.localId
            ? { ...item, quantity: action.quantity }
            : item,
        ),
      };

    default:
      return state;
  }
};
